import { Rectangle } from 'ark-common';
import { Vector2D, Dimension } from '../types/common';
import { PointerInfo } from '../../../../common/src/ts/types/uievent';
import * as ViewAction from '../actions/ViewAction';
import {
  initialViewScale, ZoomingParams, calculatePanningRange, initialViewPosition,
  PanningRange, isZoomingEnabledSheet, ZoomableSheetLayoutData, getMovableBounds, getWindowRect,
  ScrollBase, SheetLayout,
} from '../utils/view';
import { getSheetLayoutData } from '../helpers/viewGlue';
import { UIMode } from '../../../../common/src/ts/types/UIMode';
import { drawingAreaMargin } from '../drawing/constants';
import { PagesState, isPopupPanelID } from './pages';
import { Publication } from '../types/epub';
import { getDefaultRendition } from '../utils/epub';
import { maxZoomRate } from '../constants';

namespace Work {
  // iOS GestureEvent
  interface WorkingStateGesture {
    readonly type: 'gesture';
    readonly startScale: number;
    readonly zoomStart: Vector2D;
    readonly zoomDest: Vector2D;
    readonly pinchStart: Vector2D;
  }

  export interface WorkingStatePinch {
    readonly type: 'zoom';
    readonly prevDist: number;
    readonly zoomStartDist: number;
    readonly zoomStartScale: number;
    readonly zoomStart: Vector2D;
    readonly zoomDest: Vector2D;
    readonly pinchStart: Vector2D;

    readonly pointers: ReadonlyArray<PointerInfo>;
  }

  export interface WorkingStateDrag {
    readonly type: 'drag';
    readonly prevX: number;
    readonly prevY: number;
    readonly prevPrevX: number;
    readonly prevPrevY: number;
    readonly prevTimestamp: number;
    readonly prevPrevTimestamp: number;
    readonly panningRange: PanningRange;

    readonly pointer: PointerInfo; // dragからzoomに変化することがあるので必要
  }

  export interface WorkingStateInertia {
    readonly type: 'inertia';
    readonly velocityX: number;
    readonly velocityY: number;
    readonly panningRange: PanningRange;
    readonly prevTimestamp: number;
  }

  interface WorkingStateBlank {
    readonly type: 'null';
  }

  export type WorkingState =
    WorkingStateGesture | WorkingStatePinch | WorkingStateDrag | WorkingStateInertia |
    WorkingStateBlank;

  export const blankWorkingState: Readonly<WorkingStateBlank> = { type: 'null' };
}

export interface SingleViewState {
  readonly zoom: number;
  readonly panX: number;
  readonly panY: number;
  readonly gestureStartTimestamp: number | null;

  readonly work: Readonly<Work.WorkingState>;
}

export interface ViewState {
  readonly tabPanelAreaRect: Rectangle;
  readonly popupRects: { readonly [panelID: string]: Rectangle };

  // FIXME : tabStatesという名前だがpopupの状態も含む
  readonly tabStates: { readonly [panelID: string]: SingleViewState };

  readonly frameContentSizes: { readonly [panelID: string]: Dimension };
}

const createDefaultSingleViewState = (params: ZoomingParams): SingleViewState => {
  const zoom = initialViewScale(params);
  const position = initialViewPosition(params, zoom);

  return {
    zoom,
    panX: position.x,
    panY: position.y,
    gestureStartTimestamp: null,
    work: Work.blankWorkingState,
  };
};

export const getOrCreateTargetViewState = (
  state: Readonly<ViewState>,
  panelID: string,
  layoutInfo: ZoomingParams,
): SingleViewState => {
  const found = state.tabStates[panelID];
  if (found !== undefined) {
    return found;
  }

  // calculate initial transform
  return createDefaultSingleViewState(layoutInfo);
};

export const initViewState = (): ViewState => ({
  tabPanelAreaRect: { x: 0, y: 0, width: 0, height: 0 },
  popupRects: {},
  tabStates: {},
  frameContentSizes: {},
});

const countPointers = (pointers: ReadonlyArray<PointerInfo>): number => {
  return pointers.filter(p => p !== undefined).length;
};

const calcDistance = (p1: PointerInfo, p2: PointerInfo): number => {
  const dx = Math.abs(p1.windowX - p2.windowX);
  const dy = Math.abs(p1.windowY - p2.windowY);
  return Math.sqrt(dx * dx + dy * dy);
};

const calcCenter = (p1: PointerInfo, p2: PointerInfo, origin: Vector2D): Vector2D => {
  const x = (p1.windowX + p2.windowX) / 2 - origin.x;
  const y = (p1.windowY + p2.windowY) / 2 - origin.y;

  return { x, y };
};

// PointerInfoはwindow-spaceの座標なので、screenXの変換はここでは不要。
// また、iOSのGestureEventはscreen基準だが、iOSではscreenとwindowはほぼ一致すると考えて良い。
// ビューアの表示領域はウィンドウ位置から差分がある（URLバー等）が、正確に取得するのが困難な割に影響は小さい
// （ピンチズームのフォーカス位置が多少ずれる程度）ため、簡易的にウィンドウ位置基準で計算する。
const getViewerOffset = (): Vector2D => ({ x: 0, y: 0 });

const toViewerSpace = (screenX: number, screenY: number, origin: Vector2D): Vector2D => {
  const x = screenX - origin.x;
  const y = screenY - origin.y;

  return { x, y };
};

const startPinchZoom = (state: Readonly<SingleViewState>, pointers: ReadonlyArray<PointerInfo>,
                        dist: number, pinchCenter: Vector2D): SingleViewState => {
  return {
    ...state,
    work: {
      pointers,
      type: 'zoom',
      prevDist: dist,
      zoomStartDist: dist,
      zoomStartScale: state.zoom,
      zoomStart: { x: state.panX, y: state.panY },
      zoomDest: toSheetCoords(state, pinchCenter),
      pinchStart: pinchCenter,
    },
  };
};

const startDragging = (state: Readonly<SingleViewState>,
                       action: ViewAction.PointerAction,
                       layoutParams: ZoomingParams): SingleViewState => {
  const { windowX, windowY } = action.pointer;
  const panningRange = calculatePanningRange(layoutParams, state.zoom, drawingAreaMargin);
  return {
    ...state,
    gestureStartTimestamp: Date.now(),
    work: {
      panningRange,
      type: 'drag',
      pointer: action.pointer,
      prevX: windowX,
      prevY: windowY,
      prevPrevX: windowX,
      prevPrevY: windowY,
      prevTimestamp: action.timestamp,
      prevPrevTimestamp: action.timestamp,
    },
  };
};

const constrainPanningRange = (
  point: Readonly<Vector2D>,
  actualScale: number,
  layoutParams: Readonly<ZoomingParams>,
): Vector2D => {
  const range = calculatePanningRange(layoutParams, actualScale, drawingAreaMargin);
  return constrainPanningRangeDirect(point, range);
};

const constrainPanningRangeDirect = (point: Vector2D, range: PanningRange): Vector2D => ({
  x: Math.max(range.minX, Math.min(point.x, range.maxX)),
  y: Math.max(range.minY, Math.min(point.y, range.maxY)),
});

const processZoom = (
  state: Readonly<SingleViewState>,
  work: Readonly<Work.WorkingStatePinch>,
  params: Readonly<ZoomingParams>,
  action: ViewAction.PointerAction,
  containerOrigin: Vector2D,
): SingleViewState => {
  const { zoomStartScale, zoomStartDist, zoomStart, zoomDest, pinchStart } = work;
  const { x: zoomStartX, y: zoomStartY } = zoomStart;
  const { x: zoomDestX, y: zoomDestY } = zoomDest;
  const { x: pinchStartX, y: pinchStartY } = pinchStart;

  const dist = calcDistance(work.pointers[0], work.pointers[1]);
  const pinchCenter = calcCenter(work.pointers[0], work.pointers[1], containerOrigin);

  const ratio = dist / zoomStartDist;
  const scale = constrainScale(zoomStartScale * ratio, params);
  const panDiffX = pinchCenter.x - pinchStartX;
  const panDiffY = pinchCenter.y - pinchStartY;

  const { x, y } = constrainPanningRange(
    {
      x: zoomStartX - zoomDestX * (scale - zoomStartScale) + panDiffX,
      y: zoomStartY - zoomDestY * (scale - zoomStartScale) + panDiffY,
    },
    scale,
    params);

  const pointers = work.pointers.slice();
  pointers[action.pointer.pointerId] = action.pointer;

  return {
    ...state,
    zoom: scale,
    panX: x,
    panY: y,
    work: {
      ...work,
      pointers,
      prevDist: dist,
    },
  };
};

const processPan = (
  state: Readonly<SingleViewState>,
  work: Readonly<Work.WorkingStateDrag>,
  action: ViewAction.PointerAction): SingleViewState => {
  if (action.timestamp - work.prevTimestamp < 16) {
    /**
     * Ignore too frequent pointermove events, more frequent than the animation frame (16.7ms),
     * to avoid unstable framerate.
     *
     * NOTE: This guard essentially has no effect on Chrome >= 60: pointer events are already
     * aligned to the frame timing.
     * https://developers.google.com/web/updates/2017/06/aligning-input-events
     *
     * FIXME: Ideally the excessive events should be "aligned", not just "ignored". e.g. The last
     * pointermove before the pointerup should not be ignored.
     */
    return state;
  }

  const { prevX, prevY } = work;

  const dx = action.pointer.windowX - prevX;
  const dy = action.pointer.windowY - prevY;
  const desiredDest = { x: state.panX + dx, y: state.panY + dy };

  const { x: panX, y: panY } = constrainPanningRangeDirect(desiredDest, work.panningRange);

  return {
    ...state,
    panX,
    panY,
    work: {
      ...work,
      prevX: action.pointer.windowX,
      prevY: action.pointer.windowY,
      prevPrevX: prevX,
      prevPrevY: prevY,
      prevTimestamp: action.timestamp,
      prevPrevTimestamp: work.prevTimestamp,
    },
  };
};

const finishPan = (
  oldState: SingleViewState,
  work: Work.WorkingStateDrag,
  timestamp: number,
): SingleViewState => {
  if (timestamp - work.prevTimestamp > 300) {
    // The pointer had stopped for more than 300ms at the last point. No need to add inertia.
    return { ...oldState, work: Work.blankWorkingState };
  }

  const duration = timestamp - work.prevPrevTimestamp;
  if (duration === 0) {
    console.warn('Dragging detected in zero milliseconds: probably something went wrong.');
    return { ...oldState, work: Work.blankWorkingState };
  }

  const maxVelocity = 500;

  const deltaX = work.prevX - work.prevPrevX;
  const deltaY = work.prevY - work.prevPrevY;
  const velocityX = Math.min(maxVelocity, Math.max(-maxVelocity, deltaX / duration * 100));
  const velocityY = Math.min(maxVelocity, Math.max(-maxVelocity, deltaY / duration * 100));

  const minVelocity = 3;
  if (Math.abs(velocityX) < minVelocity && Math.abs(velocityY) < minVelocity) {
    // Finish pan immediately if velocity is smaller than min value.
    return { ...oldState, work: Work.blankWorkingState };
  }

  return {
    ...oldState,
    work: {
      velocityX,
      velocityY,
      type: 'inertia',
      panningRange: work.panningRange,
      prevTimestamp: timestamp,
    },
  };
};

const handlePointerDown = (oldState: Readonly<SingleViewState>,
                           action: ViewAction.PointerAction,
                           layoutInfo: ZoomingParams): SingleViewState => {
  const { work } = oldState;
  if (work.type === 'zoom' || work.type === 'gesture') {
    // 既に2本以上の指で操作されている場合、3本目以降は無視する
    return oldState;
  }

  const pointers = work.type === 'drag' ? [work.pointer] : [];
  pointers[action.pointer.pointerId] = action.pointer;

  if (countPointers(pointers) > 1) {
    const dist = calcDistance(pointers[0], pointers[1]);
    const center = calcCenter(pointers[0], pointers[1], getViewerOffset());
    return startPinchZoom(oldState, pointers, dist, center);
  }

  return startDragging(oldState, action, layoutInfo);
};

const handlePointerUp = (oldState: Readonly<SingleViewState>,
                         action: ViewAction.PointerAction): SingleViewState => {
  const { work } = oldState;

  if (work.type === 'drag') {
    return finishPan(oldState, work, action.timestamp);
  }

  // 手抜き: 1本でも指を上げたら、問答無用でピンチを停止してしまう
  return {
    ...oldState,
    work: Work.blankWorkingState,
  };
};

const handlePointerMove = (oldState: Readonly<SingleViewState>,
                           action: ViewAction.PointerAction,
                           layoutInfo: ZoomingParams): SingleViewState => {
  const { work } = oldState;
  if (work.type === 'null') {
    return oldState;
  }

  switch (work.type) {
    case 'zoom':
      return processZoom(oldState, work, layoutInfo, action, getViewerOffset());
    case 'drag':
      return processPan(oldState, work, action);
    case 'gesture':
    case 'inertia':
      return oldState;
  }
};

const getGazingPointInSheet = (state: Readonly<SingleViewState>,
                               containerWidth: number,
                               containerHeight: number): Vector2D => {
  return toSheetCoords(state, { x: containerWidth / 2, y: containerHeight / 2 });
};

const toSheetCoords = (state: SingleViewState, pointInViewer: Vector2D): Vector2D => {
  const { panX, panY, zoom } = state;
  return {
    x: (pointInViewer.x - panX) / zoom,
    y: (pointInViewer.y - panY) / zoom,
  };
};

const toSheetCoordsRect = (state: SingleViewState, rectInViewer: Rectangle): Rectangle => {
  const { panX, panY, zoom } = state;
  return {
    x: (rectInViewer.x - panX) / zoom,
    y: (rectInViewer.y - panY) / zoom,
    width: rectInViewer.width / zoom,
    height: rectInViewer.height / zoom,
  };
};

const calculatePanCoordinatesForScaleChange = (oldState: Readonly<SingleViewState>,
                                               newZoom: number,
                                               layoutInfo: Readonly<ZoomingParams>): Vector2D => {
  const { containerWidth, containerHeight } = layoutInfo;
  const gazingPoint = getGazingPointInSheet(oldState, containerWidth, containerHeight);
  const desiredCoords = {
    x: containerWidth / 2 - gazingPoint.x * newZoom,
    y: containerHeight / 2 - gazingPoint.y * newZoom,
  };
  return constrainPanningRange(desiredCoords, newZoom, layoutInfo);
};

const constrainScale = (desiredScale: number, layoutInfo: Readonly<ZoomingParams>): number => {
  const minScale = initialViewScale(layoutInfo);
  return Math.max(minScale, Math.min(desiredScale, minScale * maxZoomRate));
};

// ズームイン・ズームアウトでセットすべき値を計算
// 倍率は1～6倍とし、1倍ずつ増えていくように調整する
const calculateStepZoom = (
  currentZoom: number,
  layoutInfo: Readonly<ZoomingParams>,
  zoomIn: boolean,
): number => {
  const baseScale = initialViewScale(layoutInfo);
  const currentStep = currentZoom / baseScale;
  let nextStep: number;
  if (zoomIn) {
    // 小数誤差でcurrentStepが2.99998などになって永遠にズームインできない問題を防ぐため、0.001を加算。
    nextStep = Math.floor(currentStep + 0.001) + 1;
  } else {
    nextStep = Math.ceil(currentStep - 0.001) - 1;
  }
  nextStep = Math.max(1, Math.min(maxZoomRate, nextStep));
  return nextStep * baseScale;
};

// ツールバーボタンによるズームイン
const handleZoomIn = (oldState: Readonly<SingleViewState>,
                      _action: Readonly<ViewAction.ViewAction>,
                      layoutInfo: Readonly<ZoomingParams>) => {
  const zoom = calculateStepZoom(oldState.zoom, layoutInfo, true);
  if (zoom === oldState.zoom) {
    return oldState;
  }
  const { x: panX, y: panY } = calculatePanCoordinatesForScaleChange(oldState, zoom, layoutInfo);
  return { ...oldState, zoom, panX, panY };
};

// ツールバーボタンによるズームアウト
const handleZoomOut = (oldState: Readonly<SingleViewState>,
                       _action: Readonly<ViewAction.ViewAction>,
                       layoutInfo: Readonly<ZoomingParams>) => {
  const zoom = calculateStepZoom(oldState.zoom, layoutInfo, false);
  if (zoom === oldState.zoom) {
    return oldState;
  }
  const { x: panX, y: panY } = calculatePanCoordinatesForScaleChange(oldState, zoom, layoutInfo);
  return { ...oldState, zoom, panX, panY };
};

// ツールバーのズームスライダーによるズーム操作
const handleSetZoom = (oldState: Readonly<SingleViewState>,
                       action: ViewAction.SetZoomAction,
                       layoutInfo: ZoomingParams) => {
  const zoom = constrainScale(action.zoom, layoutInfo);
  if (zoom === oldState.zoom) {
    return oldState;
  }
  const { x: panX, y: panY } = calculatePanCoordinatesForScaleChange(oldState, zoom, layoutInfo);
  return { ...oldState, zoom, panX, panY };
};

// ツールバーボタンによるズーム/パンリセット
const handleResetZoom = (_oldState: Readonly<SingleViewState>,
                         _action: ViewAction.ViewAction,
                         layoutInfo: ZoomingParams) => {
  return createDefaultSingleViewState(layoutInfo);
};

const handleZoomRegion = (
  oldState: SingleViewState,
  action: ViewAction.ZoomRegionAction,
  layoutInfo: ZoomingParams,
): SingleViewState => {
  const { region } = action;

  // Calculate zoom
  const rectInSheetCoords = toSheetCoordsRect(oldState, region);
  const desiredScale = Math.min(
    layoutInfo.containerWidth / rectInSheetCoords.width,
    layoutInfo.containerHeight / rectInSheetCoords.height,
  );
  const actualScale = constrainScale(desiredScale, layoutInfo);

  // Adjust center position
  const center = {
    x: rectInSheetCoords.x + rectInSheetCoords.width / 2,
    y: rectInSheetCoords.y + rectInSheetCoords.height / 2,
  };

  const desiredNewPan = {
    x: layoutInfo.containerWidth / 2 - center.x * actualScale,
    y: layoutInfo.containerHeight / 2 - center.y * actualScale,
  };
  const actualPan = constrainPanningRange(desiredNewPan, actualScale, layoutInfo);

  return {
    ...oldState,
    zoom: actualScale,
    panX: actualPan.x,
    panY: actualPan.y,
  };
};

// iOS Safariのみ: gesturestart
const handleGestureStart = (oldState: Readonly<SingleViewState>,
                            action: ViewAction.GestureAction,
                            layoutInfo: ZoomingParams): SingleViewState => {
  if (oldState.work.type === 'gesture') {
    // iOS Safariにおいて、iframeをまたがったgestureを取ると、gesturechangeではなく
    // gesturestartが繰り返し呼ばれる. これは事実上のgesturechange.
    return handleGestureChange(oldState, action, layoutInfo);
  }

  const { panX, panY } = oldState;
  const pinchCenter = toViewerSpace(action.screenX, action.screenY, getViewerOffset());

  return {
    ...oldState,
    gestureStartTimestamp: Date.now(),
    work: {
      type: 'gesture',
      startScale: oldState.zoom,
      zoomStart: { x: panX, y: panY },
      zoomDest: toSheetCoords(oldState, pinchCenter),
      pinchStart: pinchCenter,
    },
  };
};

// iOS Safariのみ: gesturechange
const handleGestureChange = (
  oldState: Readonly<SingleViewState>,
  action: ViewAction.GestureAction,
  layoutInfo: ZoomingParams,
): SingleViewState => {
  const { work } = oldState;
  if (work.type !== 'gesture') {
    return oldState;
  }

  const { startScale, zoomStart, zoomDest, pinchStart } = work;
  const { x: zoomStartX, y: zoomStartY } = zoomStart;
  const { x: zoomDestX, y: zoomDestY } = zoomDest;
  const { x: pinchStartX, y: pinchStartY } = pinchStart;

  const pinchCenter = toViewerSpace(action.screenX, action.screenY, getViewerOffset());

  const ratio = action.scale;
  const scale = constrainScale(startScale * ratio, layoutInfo);
  const panDiffX = pinchCenter.x - pinchStartX;
  const panDiffY = pinchCenter.y - pinchStartY;

  const { x, y } = constrainPanningRange(
    {
      x: zoomStartX - zoomDestX * (scale - startScale) + panDiffX,
      y: zoomStartY - zoomDestY * (scale - startScale) + panDiffY,
    },
    scale,
    layoutInfo);

  return {
    ...oldState,
    zoom: scale,
    panX: x,
    panY: y,
  };
};

// iOS Safariのみ: gestureend
const handleGestureEnd = (oldState: Readonly<SingleViewState>,
                          _action: ViewAction.GestureAction): SingleViewState => {
  return {
    ...oldState,
    work: Work.blankWorkingState,
  };
};

const isFXLPopup = (panelID: string, pages: PagesState, publication: Publication) => {
  if (isPopupPanelID(panelID)) {
    if (panelID in pages.popups) {
      const path = pages.popups[panelID].path;
      const item = getDefaultRendition(publication).itemIndices[path];
      return item.fxl;
    }
  }
  return false;
};

const updateContentSize = (
  state: Readonly<ViewState>,
  action: Readonly<ViewAction.SetContentSizeAction>,
  pages: PagesState,
  publication: Publication,
): ViewState => {
  const old = state.frameContentSizes[action.panelID];
  if (old !== undefined && old.width === action.width && old.height === action.height) {
    return state;
  }

  const frameContentSizes = { ...state.frameContentSizes };
  const newSize = { width: action.width, height: action.height };
  frameContentSizes[action.panelID] = newSize;

  // FXLのpopupからnotifyContentSizeが呼ばれた場合、ポップアップ自体をリサイズする
  // この際、ズーム・パンは初期化してしまう
  if (isFXLPopup(action.panelID, pages, publication)) {
    const popupRect = state.popupRects[action.panelID];
    if (popupRect === undefined) {
      throw new Error('Unexpected state.');
    }
    const newTabStates = { ...state.tabStates };
    delete newTabStates[action.panelID];
    return {
      ...state,
      frameContentSizes,
      tabStates: newTabStates,
      popupRects: {
        ...state.popupRects,
        [action.panelID]: { ...popupRect, width: action.width, height: action.height },
      },
    };
  }

  const tabState = state.tabStates[action.panelID];
  if (tabState === undefined) {
    return {
      ...state,
      frameContentSizes,
    };
  }

  // Update tabState.
  const sheetLayout = getSheetLayoutData(publication, pages, newSize, action.panelID);
  if (!isZoomingEnabledSheet(sheetLayout)) {
    throw new Error('updateContentSize must not be called from zooming disabled sheet.');
  }
  const container = getContainerRect(state, action.panelID);
  const newLayoutInfo = {
    ...sheetLayout,
    containerWidth: container.width,
    containerHeight: container.height,
  };

  // 手抜きで普通のズームボタンからのズームと同じ中央拡大するメソッドを使っている
  // TODO: コンテンツサイズ変更時のスクロール位置追従方法を決めて newPan を返す
  // calculatePanCoordinatesForContentSizeChange を作る
  let desiredNewPan = calculatePanCoordinatesForScaleChange(
    state.tabStates[action.panelID], tabState.zoom, newLayoutInfo,
  );
  // 本文ビュー用の設定で端に寄せた初期位置の場合に、サイズ変更時されると中央拡大では端から離れて見栄えが悪い
  // なので、なるべくscrollBaseに合わせた位置を追従するようにしている
  if (newLayoutInfo.layout === SheetLayout.Fixed) {
    const scaleX = newSize.width / old.width;
    const scaleY = newSize.height / old.height;
    if (newLayoutInfo.basePosition === ScrollBase.LeftIfLargerThanScreen) {
      desiredNewPan = {
        x: tabState.panX * scaleX,
        y: tabState.panY * scaleY,
      };
    } else if (newLayoutInfo.basePosition === ScrollBase.RightIfLargerThanScreen) {
      desiredNewPan = {
        x: tabState.panX * scaleX + (1 - scaleX) * container.width,
        y: tabState.panY * scaleY + (1 - scaleY) * container.height,
      };
    }
  }

  const newPan = constrainPanningRange(desiredNewPan, tabState.zoom, newLayoutInfo);
  const newTabStates = { ...state.tabStates };
  newTabStates[action.panelID] = { ...tabState, panX: newPan.x, panY: newPan.y };
  return {
    ...state,
    frameContentSizes,
    tabStates: newTabStates,
  };
};

// Curry化したいところだが速度優先でaction引数をそのまま渡す
type SingleViewStateUpdater<T extends ViewAction.ViewAction> = (
  oldState: Readonly<SingleViewState>,
  action: Readonly<T>,
  layoutInfo: Readonly<ZoomingParams>,
) => SingleViewState;

const getContainerRect = (state: Readonly<ViewState>, panelID: string) => {
  if (isPopupPanelID(panelID)) {
    if (state.popupRects[panelID] === undefined) {
      throw new Error('Popup size is not available.');
    }
    return state.popupRects[panelID];
  }
  return state.tabPanelAreaRect;
};

// FIXME: updateSingleStateと中身がほとんど同じ
const ensureSingleState = (
  state: Readonly<ViewState>,
  action: Readonly<ViewAction.SimpleViewAction>,
  pages: PagesState,
  publication: Publication,
): ViewState => {
  if (state.tabStates[action.activePanel] != null) {
    return state;
  }

  const frameSize = state.frameContentSizes[action.activePanel];
  const sheetLayout = getSheetLayoutData(publication, pages, frameSize, action.activePanel);
  if (!isZoomingEnabledSheet(sheetLayout)) {
    return state;
  }

  const container = getContainerRect(state, action.activePanel);
  const layoutInfo: ZoomingParams = {
    ...sheetLayout,
    containerWidth: container.width,
    containerHeight: container.height,
  };
  const newState = getOrCreateTargetViewState(state, action.activePanel, layoutInfo);

  const newTabStates = { ...state.tabStates };
  newTabStates[action.activePanel] = newState;

  return { ...state, tabStates: newTabStates };
};

const updateSingleState = <T extends ViewAction.PerTabViewAction>(
  state: Readonly<ViewState>,
  action: Readonly<T>,
  pages: PagesState,
  publication: Publication,
  filter: SingleViewStateUpdater<T>,
): ViewState => {
  const frameSize = state.frameContentSizes[action.activePanel];
  const sheetLayout = getSheetLayoutData(publication, pages, frameSize, action.activePanel);
  if (!isZoomingEnabledSheet(sheetLayout)) {
    console.error('unsupported operation');
    return state;
  }

  const container = getContainerRect(state, action.activePanel);
  const layoutInfo: ZoomingParams = {
    ...sheetLayout,
    containerWidth: container.width,
    containerHeight: container.height,
  };
  const oldState = getOrCreateTargetViewState(state, action.activePanel, layoutInfo);

  const newState = filter(oldState, action, layoutInfo);
  if (newState === oldState) {
    return state;
  }

  const newTabStates = { ...state.tabStates };
  newTabStates[action.activePanel] = newState;

  return { ...state, tabStates: newTabStates };
};

// TODO: Use SingleViewStateUpdater
const updatePan = (
  state: Readonly<ViewState>,
  action: ViewAction.SetPanAction,
  pages: PagesState,
  publication: Publication,
): ViewState => {
  const frameSize = state.frameContentSizes[action.activePanel];
  const sheetLayout = getSheetLayoutData(publication, pages, frameSize, action.activePanel);
  if (!isZoomingEnabledSheet(sheetLayout)) {
    console.error('unsupported operation');
    return state;
  }

  const container = getContainerRect(state, action.activePanel);
  const layoutInfo: ZoomingParams = {
    ...sheetLayout,
    containerWidth: container.width,
    containerHeight: container.height,
  };
  const oldState = getOrCreateTargetViewState(state, action.activePanel, layoutInfo);

  const { x: panX, y: panY } = constrainPanningRange(
    { x: action.x, y: action.y },
    oldState.zoom,
    { ...layoutInfo, enableMargin: false },
  );

  const newState = { ...oldState, panX, panY };
  if (newState === oldState) {
    return state;
  }

  const newTabStates = { ...state.tabStates };
  newTabStates[action.activePanel] = newState;

  return { ...state, tabStates: newTabStates };
};

const updateTabStateForContainerResize = (
  state: Readonly<SingleViewState>,
  sheetLayout: Readonly<ZoomableSheetLayoutData>,
  oldWidth: number,
  oldHeight: number,
  newWidth: number,
  newHeight: number,
): SingleViewState => {
  const oldLayoutInfo = { ...sheetLayout, containerWidth: oldWidth, containerHeight: oldHeight };
  const newLayoutInfo = { ...sheetLayout, containerWidth: newWidth, containerHeight: newHeight };

  const zoomRatio = state.zoom / initialViewScale(oldLayoutInfo);
  const center = getGazingPointInSheet(state, oldWidth, oldHeight);

  const defaultScaleInNewDimension = initialViewScale(newLayoutInfo);
  const newScale = defaultScaleInNewDimension * zoomRatio;

  const desiredNewCoords = {
    x: newWidth / 2 - center.x * newScale,
    y: newHeight / 2 - center.y * newScale,
  };
  const newCoords = constrainPanningRange(desiredNewCoords, newScale, newLayoutInfo);

  return {
    ...state,
    zoom: newScale,
    panX: newCoords.x,
    panY: newCoords.y,
  };
};

const updateContainerRect = (
  state: Readonly<ViewState>,
  pages: PagesState,
  publication: Publication,
  newRect: Rectangle,
): ViewState => {
  // XXX : ビューア領域(<div id="react-root">)サイズは変わらず
  // ウィンドウサイズだけ変化する場合は考慮していない
  if (newRect.width === state.tabPanelAreaRect.width &&
    newRect.height === state.tabPanelAreaRect.height) {
    return state;
  }

  const { tabStates } = state;
  const newTabStates: { [panelID: string]: SingleViewState } = {};

  for (const panelID in state.tabStates) {
    if (isPopupPanelID(panelID)) {
      continue;
    }
    const oldTabState = tabStates[panelID];
    const frameSize = state.frameContentSizes[panelID];
    const sheetLayout = getSheetLayoutData(publication, pages, frameSize, panelID);
    if (!isZoomingEnabledSheet(sheetLayout)) {
      throw new Error('Unexpected state: view state for zooming disabled sheet found.');
    }

    newTabStates[panelID] = updateTabStateForContainerResize(
      oldTabState,
      sheetLayout,
      state.tabPanelAreaRect.width,
      state.tabPanelAreaRect.height,
      newRect.width,
      newRect.height);
  }

  // ポップアップを位置制限
  const newPopupRects = { ...state.popupRects };
  for (const popupID in state.popupRects) {
    const popupRect = state.popupRects[popupID];
    const topLevel = pages.popups[popupID].topLevel;
    const rootContainerRect = topLevel ? getWindowRect() : newRect;

    const movableBounds = getMovableBounds(popupRect, rootContainerRect);
    const { x, y } = newPopupRects[popupID];
    newPopupRects[popupID] = {
      ...newPopupRects[popupID],
      x: Math.min(movableBounds.right, Math.max(x, movableBounds.left)),
      y: Math.min(movableBounds.bottom, Math.max(y, movableBounds.top)),
    };
  }

  return {
    ...state,
    popupRects: newPopupRects,
    tabPanelAreaRect: newRect,
    tabStates: newTabStates,
  };
};

const updatePopupRect = (
  state: Readonly<ViewState>,
  pages: PagesState,
  publication: Publication,
  id: string,
  newRect: Rectangle,
): ViewState => {
  const oldRect = state.popupRects[id];
  if (oldRect === undefined) {
    return { ...state, popupRects: { ...state.popupRects, [id]: newRect } };
  }

  if (
    newRect.width === oldRect.width &&
    newRect.height === oldRect.height &&
    newRect.x === oldRect.x &&
    newRect.y === oldRect.y
  ) {
    return state;
  }

  if (newRect.width === oldRect.width && newRect.height === oldRect.height) {
    return { ...state, popupRects: { ...state.popupRects, [id]: newRect } };
  }

  // update tab state
  const oldTabState = state.tabStates[id];
  if (oldTabState === undefined) {
    return { ...state, popupRects: { ...state.popupRects, [id]: newRect } };
  }

  const frameSize = state.frameContentSizes[id];
  const sheetLayout = getSheetLayoutData(publication, pages, frameSize, id);
  if (!isZoomingEnabledSheet(sheetLayout)) {
    throw new Error('Unexpected state: view state for non-zoomable popup found.');
  }

  const newTabStates = { ...state.tabStates };
  newTabStates[id] = updateTabStateForContainerResize(
    oldTabState,
    sheetLayout,
    oldRect.width,
    oldRect.height,
    newRect.width,
    newRect.height,
  );

  return { ...state, tabStates: newTabStates, popupRects: { ...state.popupRects, [id]: newRect } };
};

const applyInertia = (
  state: SingleViewState,
  work: Work.WorkingStateInertia,
  timestamp: number,
): SingleViewState => {
  const duration = timestamp - work.prevTimestamp;
  if (duration < 1) {
    return state;
  }

  // 時間がたった分だけ多く移動させる(最大100ms分)
  const dx = work.velocityX * Math.min(5, duration / 20);
  const dy = work.velocityY * Math.min(5, duration / 20);
  const desiredDest = { x: state.panX + dx, y: state.panY + dy };
  const { x: panX, y: panY } = constrainPanningRangeDirect(desiredDest, work.panningRange);

  let eased;
  if (Math.abs(panX - state.panX) > 1 || Math.abs(panY - state.panY) > 1) {
    const slowdownRatio = Math.pow(0.9, duration / 17); // slow down 10% per 17ms
    eased = {
      ...work,
      velocityX: work.velocityX * slowdownRatio,
      velocityY: work.velocityY * slowdownRatio,
      prevTimestamp: timestamp,
    };
  } else {
    eased = Work.blankWorkingState;
  }

  return { ...state, panX, panY, work: eased };
};

const handleAnimationFrame = (
  state: ViewState,
  action: ViewAction.AnimationFrameAction,
): ViewState => {
  const newTabStates = { ...state.tabStates };
  let updated = false;

  for (const panelID in newTabStates) {
    const singleState = newTabStates[panelID];
    if (singleState.work.type === 'inertia') {
      newTabStates[panelID] = applyInertia(singleState, singleState.work, action.timestamp);
      updated = true;
    }
  }

  if (updated) {
    return { ...state, tabStates: newTabStates };
  }

  return state;
};

export const view = (
  state: Readonly<ViewState>,
  action: Readonly<ViewAction.ViewAction>,
  pages: PagesState,
  publication: Publication,
  uimode: UIMode,
): ViewState => {
  if (action.type !== ViewAction.VIEW_SET_TAB_PANEL_AREA_RECT) {
    if (state.tabPanelAreaRect.width === 0 && state.tabPanelAreaRect.height === 0) {
      // TODO : Adopt more robust way and reduce warning log.
      console.warn('Skipping view action until the container size is determined.');
      return state;
    }
  }
  switch (action.type) {
    case ViewAction.VIEW_SET_TAB_PANEL_AREA_RECT:
      return updateContainerRect(state, pages, publication, action.rect);
    case ViewAction.VIEW_SET_POPUP_RECT:
      return updatePopupRect(state, pages, publication, action.id, action.rect);
    case ViewAction.VIEW_ANIMATION_FRAME:
      return handleAnimationFrame(state, action);
    case ViewAction.VIEW_ZOOM_IN:
      // XXX[TS Bug?]: なぜか型を明示しないとPanActionなどに誤解されてエラーになる
      return updateSingleState<ViewAction.SimpleViewAction>(
        state, action, pages, publication, handleZoomIn);
    case ViewAction.VIEW_ZOOM_OUT:
      return updateSingleState<ViewAction.SimpleViewAction>(
        state, action, pages, publication, handleZoomOut);
    case ViewAction.VIEW_SET_ZOOM:
      return updateSingleState(state, action, pages, publication, handleSetZoom);
    case ViewAction.VIEW_RESET_ZOOM:
      return updateSingleState(state, action, pages, publication, handleResetZoom);
    case ViewAction.VIEW_ZOOM_REGION:
      return updateSingleState(state, action, pages, publication, handleZoomRegion);
    case ViewAction.VIEW_SET_PAN:
      return updatePan(state, action, pages, publication);
    case ViewAction.VIEW_SET_CONTENT_SIZE:
      return updateContentSize(state, action, pages, publication);
  }

  // Ignore UI events on "Mouse" mode
  // TODO : Probably there is a better solution.
  if (uimode === UIMode.Mouse) {
    return state;
  }

  switch (action.type) {
    case ViewAction.VIEW_ENSURE_STATE:
      return ensureSingleState(state, action, pages, publication);
    case ViewAction.VIEW_GESTURE_START:
      return updateSingleState(state, action, pages, publication, handleGestureStart);
    case ViewAction.VIEW_GESTURE_CHANGE:
      return updateSingleState(state, action, pages, publication, handleGestureChange);
    case ViewAction.VIEW_GESTURE_END:
      return updateSingleState(state, action, pages, publication, handleGestureEnd);
    case ViewAction.VIEW_POINTER_DOWN:
      return updateSingleState(state, action, pages, publication, handlePointerDown);
    case ViewAction.VIEW_POINTER_UP:
      return updateSingleState(state, action, pages, publication, handlePointerUp);
    case ViewAction.VIEW_POINTER_MOVE:
      return updateSingleState(state, action, pages, publication, handlePointerMove);
  }
};
